import { t } from 'i18next';
import { Color, Vector3 } from 'three';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { isDefined } from '../../../../../../../utils/variables';
import { CoordinateManager } from '../../../../../../coordinate-manager';
import { RevertChange } from '../../../../../../elements/design/design-node/design-node';
import { addCircle } from '../../../../../../elements/design/design-node/utils/circle';
import { getCircleConics } from '../../../../../../elements/design/design-node/utils/conic';
import {
  findLineSegmentIdFromIntersection,
  LineSegmentId,
} from '../../../../../../elements/design/design-node/utils/line-segment-node';
import {
  findPointIdFromIntersection,
  PointId,
} from '../../../../../../elements/design/design-node/utils/point-node';
import { ActionInProgress, ActionName, DesignTool } from '../../../../../design-tool';
import { PickingLine } from '../../../../../picking-line';
import { PickingSphere } from '../../../../../picking-sphere';
import { addAndCommitAction } from '../../utils/actions';
import { getConicCursorVertices } from '../../utils/curve';
import { createNewPoint } from '../../utils/point';
import { abortActionInProgress, applyActionInProgress, initActionInProgress } from '../general';

type CircleCenter = {
  position: Vector3;
  pointId?: PointId;
  lineSegmentId?: LineSegmentId;
};

/**
 * Gets the radius from the center to the point, in the horizontal plane, since the circle lies in
 * the horizontal plane through its center.
 */
export const getCircleRadius = (center: Vector3, point: Vector3) =>
  Math.hypot(point.x - center.x, point.y - center.y);

/**
 * Creates a circle in two clicks: the center, and a point on the circle. The radius can also be
 * typed in the tooltip.
 */
export const createCircle = (tool: DesignTool): ContextMenuItem => {
  const centerPoint = tool.currentPoint?.clone();
  const disabled = !isDefined(centerPoint);

  const center: CircleCenter | undefined = centerPoint && {
    position: centerPoint,
    pointId: findPointIdFromIntersection(tool.asset.designNode, tool.intersection),
    lineSegmentId: findLineSegmentIdFromIntersection(tool.asset.designNode, tool.intersection),
  };

  return {
    title: t('contextMenu.create.items.circle', { ns: 'skyviewDesign' }),
    action: () => {
      if (disabled || !center) {
        return;
      }

      const centerCursor = new PickingSphere(new Color(0.75, 0.75, 0.75));
      centerCursor.position.copy(center.position);

      const circleCursor = new PickingLine();

      // The radius (meters), when it is typed.
      let typedRadius: number | undefined;

      const getRadius = () =>
        typedRadius ??
        (tool.currentPoint ? getCircleRadius(center.position, tool.currentPoint) : 0);

      const finish = () => void applyActionInProgress(tool).action();

      const action: ActionInProgress = {
        actionName: ActionName.CreateCircle,
        initiate: () => {
          tool.add(centerCursor);
          tool.add(circleCursor);
          tool.onEdited?.();

          tool.curveDistanceInput = {
            kind: 'radius',
            shape: 'circle',
            getDistance: getRadius,
            setDistance: (distance) => {
              typedRadius = distance;
              action.refresh?.();
              tool.updateTooltip();
            },
            isValid: (radius) => radius > 0,
            apply: finish,
            cancel: () => void abortActionInProgress(tool).action(),
          };
        },
        cleanup: () => {
          tool.remove(centerCursor);
          tool.remove(circleCursor);
          tool.curveDistanceInput = undefined;
        },
        refresh: () => {
          const radius = getRadius();
          circleCursor.setVertices(
            radius > 0 ? getConicCursorVertices(getCircleConics(center.position, radius)) : [],
          );
        },
        onProjectProjectionUpdated: (origoDiff) => {
          center.position.add(origoDiff);
          centerCursor.position.add(origoDiff);
        },
        readyToApply: () => getRadius() > 0,
        onMouseUp: finish,
        apply: () => {
          const radius = getRadius();

          if (radius > 0) {
            applyCircle(tool, center, radius);
          }
        },
      };

      initActionInProgress(tool, action);
      tool.contextMenuCallback?.();
    },
    disabled,
  };
};

/**
 * Creates the circle around the center: four quarter arcs, joined by seams of the point at the
 * center, see circle.ts. The center is the point there if there is one, or a new point, which
 * splits the line segment at the center.
 */
function applyCircle(tool: DesignTool, center: CircleCenter, radius: number) {
  const node = tool.asset.designNode;
  const coordinates = CoordinateManager.getInstance().localToProjectCoords(center.position);
  let changes: RevertChange[] = [];

  addAndCommitAction(tool, {
    commit: () => {
      const attributes = tool.getActiveAttributes();
      const result = createNewPoint(
        tool,
        coordinates,
        isDefined(center.pointId) ? undefined : center.lineSegmentId,
        center.pointId,
        attributes,
      );
      const conics = getCircleConics(node.getPointById(result.id).vertex, radius);

      changes = [...addCircle(node, result.id, conics, attributes), ...result.revertChanges];

      node.refresh();
      tool.asset.refreshMetadata();
    },
    rollback: () => {
      node.revert(changes);
      node.refresh();
      tool.asset.refreshMetadata();
    },
  });
}
